import {
  dateAtomToDate,
  getLoanById,
  Invitation,
  OnboardingDetailsType,
  patchUpdateInvitationByType,
  PhoneNumber,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';

import { BorrowerInvitationForm } from '@/components/modules/KYC/KYCLoan/shared/BorrowerInvitationSection/components/BorrowerInvitationForm';
import {
  BorrowerInvitationFormSchema,
  BorrowerInvitationFormType,
} from '@/components/modules/KYC/KYCLoan/shared/BorrowerInvitationSection/schemas/BorrowerInvitationFormSchema';
import { EmptyDialCodeOption } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/constants/EmptyDialCodeOption';
import { getDialCodeItemFromList } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/getDialCodeItemFromList';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { findCurrentParticipantOnboardingDetailsByOnboardingDetailsId } from '@/utils/findCurrentParticipantOnboardingDetails';
import { removeNullKeysFromObject } from '@/utils/removeNullKeysFromObject';

export const BorrowerInvitationEditDialog = ({
  borrowerInvitation,
  opened,
  setOpened,
  toggleDialog,
}: {
  borrowerInvitation: Invitation;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog;
}) => {
  const {
    state: { applicationParticipant },
  } = usePageContext();
  const { request, loading } = useRequest();
  const { loanId } = useParams();

  if (!loanId) return null;

  const { data: loan, revalidate } = useCache(getLoanById, { loanId });

  if (!loan) return <></>;
  const phoneNumber = borrowerInvitation?.phoneNumber ? new PhoneNumber(borrowerInvitation.phoneNumber) : undefined;

  const borrowerInvitationFormDefaultValues = {
    fullName: `${borrowerInvitation.fullName.firstName} ${borrowerInvitation.fullName.lastName}`,
    documentNumber: borrowerInvitation.documentNumber,
    email: borrowerInvitation.email,
    dialCode: phoneNumber ? getDialCodeItemFromList(phoneNumber.countryCallingCode) ?? EmptyDialCodeOption : undefined,
    phoneNumber: phoneNumber?.nationalNumber,
    relation: borrowerInvitation.relation,
    expiresOn: borrowerInvitation.expiresOn,
  };

  const updateInvitationByTypeBodyPreMapper = (useFormValues: BorrowerInvitationFormType) => {
    const id = loan
      ? findCurrentParticipantOnboardingDetailsByOnboardingDetailsId({
          loan: loan,
          onboardingDetailsId: applicationParticipant.onboardingDetails?.id,
        })?.id
      : undefined;
    if (!id) return null;
    return {
      id,
      type: OnboardingDetailsType.BORROWER,
      firstName: borrowerInvitation.fullName.firstName,
      lastName: borrowerInvitation.fullName.lastName,
      email: useFormValues.email,
      phoneNumber: `+${useFormValues.dialCode.dialCode}${useFormValues.phoneNumber}`,
      documentNumber: borrowerInvitation.documentNumber,
      relation: useFormValues?.relation || null,
      expiresOn: useFormValues?.expiresOn ? dateAtomToDate(useFormValues.expiresOn) : null,
    };
  };

  const onSubmitForm = async (useFormValues: BorrowerInvitationFormType) => {
    const updateInvitationByTypeBody = removeNullKeysFromObject(updateInvitationByTypeBodyPreMapper(useFormValues));
    if (!updateInvitationByTypeBody) return;
    await request(patchUpdateInvitationByType, updateInvitationByTypeBody);
    toggleDialog();
    revalidate();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_BORROWER_INVITATION'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      formSchema={BorrowerInvitationFormSchema}
      defaultValues={borrowerInvitationFormDefaultValues}
    >
      <BorrowerInvitationForm
        borrowerInvitation={borrowerInvitation}
        fullWidth
        participantRole={applicationParticipant.role}
        fieldVariant={FieldVariant.form}
      />
    </EditDialogWrapper>
  );
};
